/**
 * WelfareResourcesTab — Super-admin CMS for managing curated wellbeing resources
 * shown to coaches on the Welfare page → Resources tab.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Plus, Trash2, Heart, BookOpen, Video, Wind, Headphones, PhoneCall, BookMarked } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { value: 'article', label: 'Article', icon: BookOpen },
  { value: 'video', label: 'Video', icon: Video },
  { value: 'breathing', label: 'Breathing / Exercise', icon: Wind },
  { value: 'podcast', label: 'Podcast', icon: Headphones },
  { value: 'helpline', label: 'Helpline / Support', icon: PhoneCall },
  { value: 'book', label: 'Book', icon: BookMarked },
];

const blankNew = { title: '', description: '', url: '', type: 'article' };

export default function WelfareResourcesTab() {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newRes, setNewRes] = useState(blankNew);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/welfare/resources/all`);
      setResources(data || []);
    } catch (e) {
      toast.error('Failed to load welfare resources');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (id, field, value) => {
    setResources(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  const saveOne = async (resource) => {
    try {
      setSaving(true);
      await axios.put(`${API}/welfare/resources/${resource.id}`, {
        title: resource.title,
        description: resource.description,
        url: resource.url,
        type: resource.type,
        display_order: resource.display_order,
        active: resource.active,
      });
      toast.success('Saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const removeOne = async (resourceId) => {
    if (!window.confirm('Delete this resource?')) return;
    try {
      await axios.delete(`${API}/welfare/resources/${resourceId}`);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleCreate = async () => {
    if (!newRes.title.trim() || !newRes.url.trim()) {
      toast.error('Title and URL are required');
      return;
    }
    try {
      setCreating(true);
      await axios.post(`${API}/welfare/resources`, newRes);
      toast.success('Resource added');
      setNewRes(blankNew);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="cms-welfare-resources-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Heart className="w-5 h-5 text-rose-500" />Welfare Resources</CardTitle>
          <CardDescription>
            Manage the curated wellbeing resources shown to coaches in their Welfare → Resources tab.
            All saves are immediate; toggling "Active" off hides a resource without deleting it.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Add new */}
      <Card data-testid="cms-welfare-add-resource">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Plus className="w-4 h-4" />Add a new resource</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Title</Label>
              <Input value={newRes.title} onChange={(e) => setNewRes({ ...newRes, title: e.target.value })} placeholder="e.g. 5-minute box breathing" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={newRes.type} onValueChange={(v) => setNewRes({ ...newRes, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>URL</Label>
            <Input value={newRes.url} onChange={(e) => setNewRes({ ...newRes, url: e.target.value })} placeholder="https://..." />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              rows={2}
              value={newRes.description}
              onChange={(e) => setNewRes({ ...newRes, description: e.target.value })}
              placeholder="One line describing how this helps coaches."
            />
          </div>
          <div className="flex justify-end">
            <Button onClick={handleCreate} disabled={creating} className="bg-rose-500 hover:bg-rose-600 text-white" data-testid="cms-welfare-add-btn">
              {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Adding...</> : <><Plus className="w-4 h-4 mr-2" />Add Resource</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Existing list */}
      <div className="space-y-3">
        {resources.map(r => (
          <Card key={r.id} className={r.active ? '' : 'opacity-60'} data-testid={`cms-welfare-resource-${r.id}`}>
            <CardContent className="p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                <div className="md:col-span-4">
                  <Label className="text-xs">Title</Label>
                  <Input value={r.title} onChange={(e) => updateField(r.id, 'title', e.target.value)} />
                </div>
                <div className="md:col-span-3">
                  <Label className="text-xs">URL</Label>
                  <Input value={r.url} onChange={(e) => updateField(r.id, 'url', e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs">Type</Label>
                  <Select value={r.type} onValueChange={(v) => updateField(r.id, 'type', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-1">
                  <Label className="text-xs">Order</Label>
                  <Input
                    type="number"
                    value={r.display_order ?? 0}
                    onChange={(e) => updateField(r.id, 'display_order', parseInt(e.target.value, 10) || 0)}
                  />
                </div>
                <div className="md:col-span-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Switch checked={r.active} onCheckedChange={(v) => updateField(r.id, 'active', v)} />
                    <span className="text-xs text-gray-600">Active</span>
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Textarea
                  rows={2}
                  value={r.description || ''}
                  onChange={(e) => updateField(r.id, 'description', e.target.value)}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removeOne(r.id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid={`cms-welfare-delete-${r.id}`}
                >
                  <Trash2 className="w-4 h-4 mr-1" />Delete
                </Button>
                <Button
                  size="sm"
                  onClick={() => saveOne(r)}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid={`cms-welfare-save-${r.id}`}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
